<script setup lang="ts">
import type { ColumnFiltersState } from '@tanstack/vue-table'
import TableRowElemental from '../TableRowElemental/index.vue'
import TableRowAccordeonElementalsAllList from '../TableRowAccordeonElementalsAllList/index.vue'
import ListsLoader from '~~/src/components/DrawerSidebarRight/components/shared/ListsLoader/index.vue'
import IconBox from '~~/src/components/DrawerSidebarRight/components/shared/IconBox.vue'
import ElementalElemetalFolderIconPicker from '~~/src/components/Elemental/ElemetalFolderIconPicker/index.vue'
import type { ElementalTypeEnum } from '~~/src/types/elemental'
import { Button } from '@/components/ui/button'
import {
  TableCell,
  TableRow,
} from '@/components/ui/table'

const props = defineProps<{
  elementalTypeId: ElementalTypeEnum
  columns: string[]
  label: string
  colspan: number
  isPending: boolean
  filters: ColumnFiltersState
}>()

const emits = defineEmits<{
  (e: 'createElemental', elementalTypeId: ElementalTypeEnum, label: string): void
}>()

const handlerCreateElemental = () => {
  emits('createElemental', props.elementalTypeId, props.label)
}
</script>

<template>
  <ListsLoader mode="library" :type="props.elementalTypeId" :filters="props.filters">
    <template #default="{ lists }">
      <div v-if="lists && lists?.length > 0" class="wrapper-lists">
        <template v-for="(list, index) in lists" :key="`${list.id}-${index}-${props.label}`">
          <template v-if="list.id === 'all'">
            <TableRowAccordeonElementalsAllList
              v-if="elementalTypeId !== 0"
              :is-loading="false"
              :elementals-type="props.elementalTypeId"
              :elementals="list.prompts"
              :colspan="props.colspan"
              :is-static="false"
              :list="list"
              title="Ungrouped"
              :columns="props.columns"
            />
          </template>
          <TableRow
            v-else
            class="block border-none"
          >
            <TableCell
              :colspan="props.colspan"
              class="block p-0 border-none"
            >
              <TableRowElemental
                :elemental-type="list.elementalTypeId ?? 0"
                :list="list"
                :title="list.title"
                :cells-length="props.colspan"
                :elemental-label="props.label"
                :columns="props.columns"
                :filters="props.filters"
              >
                <template #list-icon>
                  <IconBox class="h-7 w-7 flex-shrink-0 !p-0">
                    <ElementalElemetalFolderIconPicker :type="list.contentTypeId ?? 0" />
                  </IconBox>
                </template>
              </TableRowElemental>
            </TableCell>
          </TableRow>
        </template>
      </div>

      <div v-else class="text-center space-y-4 p-4 bg-[#F6F6FF]">
        <p class="text-gray-500 text-sm">
          You don't have any <span class="font-semibold capitalize">{{ props.label }}</span> yet.
        </p>

        <Button
          variant="snack-icon"
          icon="sm"
          class="text-soft-black font-bold text-xs"
          :disabled="isPending"
          @click="handlerCreateElemental"
        >
          <span class="flex items-center justify-center gap-2">
            <div v-if="!isPending" class="i-material-symbols:add w-5 h-5" />
            <div v-else class="i-mdi:loading animate-spin w-4 h-4" />
            Add {{ props.label }}
          </span>
        </Button>
      </div>
    </template>
  </ListsLoader>
</template>

<style lang="scss" scoped>
.wrapper-lists {
  :deep(.row-elemental) {
    padding-left: 2rem;
  }
}
</style>
